/**
 * useUnsavedChangesGuard — two-step "discard changes?" guard for modal
 * Cancel / × / Escape flows.
 *
 * (2026-08-27, follow-up to Esc-consistency polish.)
 *
 * Contract:
 *   const { guardedClose, pill } = useUnsavedChangesGuard(isDirty, onClose);
 *   useEscapeKey(guardedClose);
 *   return (
 *     <div className="fixed inset-0 …">
 *       …
 *       <button onClick={guardedClose}>×</button>
 *       …
 *       <button onClick={guardedClose}>Cancel</button>
 *       {pill}
 *     </div>
 *   );
 *
 * Semantics:
 *   • When `isDirty === false`, `guardedClose` behaves exactly like
 *     `onClose` — no interstitial. Pristine forms dismiss instantly.
 *   • When `isDirty === true`:
 *       – First call → arm the guard, render a bottom-centered pill
 *         ("You have unsaved changes · press Esc / Cancel again to
 *         discard"), and DO NOT close.
 *       – A second call within `armWindowMs` (default 5 s) → skip
 *         the guard, disarm, and actually close.
 *       – If nothing happens within the window, the pill auto-hides
 *         and the guard resets — the next Cancel/Esc will re-arm.
 *   • The pill is rendered via a portal to `document.body`, so it
 *     visually escapes the modal card and appears above every layer
 *     (`z-[200]` sits on top of Radix Dialog's `z-50` overlay).
 *
 * The hook is completely self-contained — no dependency on `sonner`,
 * `react-toastify`, or any global toaster. Any modal can adopt it by
 * changing three lines.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function useUnsavedChangesGuard(isDirty, onClose, options = {}) {
  const { armWindowMs = 5000 } = options;
  const [armed, setArmed] = useState(false);
  const timeoutRef = useRef(null);

  const disarm = useCallback(() => {
    setArmed(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const guardedClose = useCallback(() => {
    // Clean forms — pass straight through.
    if (!isDirty) {
      disarm();
      onClose?.();
      return;
    }
    // Dirty form + already armed → user has confirmed discard.
    if (armed) {
      disarm();
      onClose?.();
      return;
    }
    // Dirty form + first attempt → arm the guard and show the pill.
    setArmed(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setArmed(false);
      timeoutRef.current = null;
    }, armWindowMs);
  }, [isDirty, armed, onClose, armWindowMs, disarm]);

  // If the form is submitted/reset while armed (isDirty flips to
  // false), auto-disarm so no stale pill sticks around.
  useEffect(() => {
    if (!isDirty && armed) disarm();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isDirty, armed, disarm]);

  const pill =
    armed && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="alert"
            aria-live="polite"
            data-testid="unsaved-changes-pill"
            // `pointer-events-none` is critical: without it the pill
            // physically overlaps the modal's own Cancel button on the
            // Y-axis and absorbs the very click the user is trying to
            // send. The pill is informational only — clicks pass
            // through to whatever the user was really aiming at.
            className="fixed z-[200] bottom-6 left-1/2 -translate-x-1/2
                       pointer-events-none
                       bg-slate-900 text-white text-[12px] font-semibold
                       px-4 py-2.5 rounded-full shadow-2xl
                       flex items-center gap-3 border border-amber-400/70
                       animate-in slide-in-from-bottom-2 fade-in duration-200"
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>You have unsaved changes</span>
            <span className="text-white/70 text-[11px] font-normal">
              · press{' '}
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono">
                Esc
              </kbd>{' '}
              or click <b className="text-white">Cancel</b> again to discard
            </span>
          </div>,
          document.body,
        )
      : null;

  return { guardedClose, pill, isArmed: armed };
}

export default useUnsavedChangesGuard;
